import { ChangeEvent, JSX, RefObject, useEffect, useRef } from 'react';
import { Box, Stack, TextField, Typography } from '@mui/material';
import { RichTextEditor, RichTextEditorRef } from 'mui-tiptap';
import useExtensions from '../hooks/extension-hooks';
import EditorControls from './EditorControls';
import { AdminMiniTarget } from '../admin-mini.types';

export interface AdminMiniMessageEditorProps {
    /** The recipient being written to, or undefined when none is chosen. */
    target?: AdminMiniTarget;
    onChange: (target: AdminMiniTarget) => void;
    disabled?: boolean;
    /** Rendered under the editor. The assistant's draft offer goes here. */
    footer?: JSX.Element;
}

/**
 * The message written for one recipient.
 *
 * **One recipient at a time, on purpose.** Both admin tools store subject and body *per target*
 * rather than once for the run, because the whole point of an admin run is that each approach can
 * be different - the same six words sent to six people is a mail-out, not six mystery shops. The
 * recipient list is the navigation and this is the page.
 *
 * The editor is uncontrolled after it mounts: tiptap owns its own document, and feeding it a new
 * `content` prop on every keystroke moves the caret to the start. So the content is set when the
 * chosen recipient changes and read back on edit, which is what `key` below arranges.
 */
export const AdminMiniMessageEditor: (props: AdminMiniMessageEditorProps) => JSX.Element =
    (props: AdminMiniMessageEditorProps): JSX.Element => {
        const extensions = useExtensions();
        const editorRef: RefObject<RichTextEditorRef | null> = useRef<RichTextEditorRef>(null);
        const { target, onChange } = props;

        // Swapping recipients has to swap the document with them. Without this the editor keeps
        // whatever it had and the next save writes one person's message onto another's.
        useEffect((): void => {
            if (target === undefined) {
                return;
            }
            const editor = editorRef.current?.editor;
            if (editor && editor.getHTML() !== target.emailBody) {
                // Tiptap 3 fires onUpdate from setContent by default, which would echo the
                // normalised HTML back through onChange as though the admin had edited it.
                editor.commands.setContent(target.emailBody ?? '', { emitUpdate: false });
            }
        }, [target?.email]);

        // **Not `height: 100%` below.** The left column of the composer is tall - sender, schedule,
        // company, Noks, recipients - and a right column that stretched to match it centred this
        // prompt 1500px down the page, below the fold of a dialog only 940px high. Measured in a
        // browser, which is the only place a grid row stretching like that is visible at all.
        if (target === undefined) {
            return (
                <Box sx={ {
                    minHeight: '320px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    padding: '24px',
                } }>
                    <Typography sx={ { fontSize: '13px', color: 'text.secondary', textAlign: 'center' } }>
                        { 'Choose a recipient to write their message.' }
                    </Typography>
                </Box>
            );
        }

        return (
            <Stack spacing={ 2 } sx={ { width: '100%' } }>
                <Box>
                    <Typography sx={ {
                        fontSize: 'var(--wf-eyebrow)',
                        letterSpacing: 'var(--wf-track)',
                        textTransform: 'uppercase',
                        color: 'text.secondary',
                        fontWeight: 700,
                        marginBottom: '8px',
                    } }>
                        { `Writing to ${ target.name === '' ? target.email : target.name }` }
                    </Typography>

                    <TextField fullWidth
                               size="small"
                               label="Subject"
                               value={ target.subject }
                               disabled={ props.disabled }
                               onChange={ (e: ChangeEvent<HTMLInputElement>): void =>
                                   onChange({ ...target, subject: e.target.value }) } />
                </Box>

                <Box className="cms-editor" sx={ {
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    '& .ms_editor__container': { minHeight: '260px' },
                } }>
                    <RichTextEditor
                        key={ target.email }
                        ref={ editorRef }
                        extensions={ extensions }
                        content={ target.emailBody }
                        editable={ props.disabled !== true }
                        editorProps={ { attributes: { class: 'ms_editor__container' } } }
                        onUpdate={ (): void => {
                            const html: string = editorRef.current?.editor?.getHTML() ?? '';
                            if (html !== target.emailBody) {
                                onChange({ ...target, emailBody: html });
                            }
                        } }
                        renderControls={ (): JSX.Element => <EditorControls /> }
                    />
                </Box>

                { props.footer }
            </Stack>
        );
    };
